import type { CollectiblesConfig } from '../config/collectibles.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { CollectibleCatalog } from './CollectibleCatalog';
import { CollectibleInstance } from './CollectibleInstance';
import { CollectibleType } from './CollectibleType';

export class CollectibleManager {
  private readonly bySegment = new Map<number, CollectibleInstance[]>();
  private nextUid = 1;

  constructor(
    private readonly catalog: CollectibleCatalog,
    private readonly config: CollectiblesConfig,
    private readonly events: EventBus<GameEvents>,
  ) {
    events.on('segment:spawned', ({ segment }) => this.spawnFor(segment));
    events.on('segment:recycled', ({ segment }) => this.despawnFor(segment));
  }

  /** Uncollected collectibles on committed segments overlapping the track-distance range. */
  query(minZ: number, maxZ: number, out: CollectibleInstance[] = []): CollectibleInstance[] {
    out.length = 0;
    for (const list of this.bySegment.values()) {
      for (const item of list) {
        if (item.collected || !item.segment.committed) continue;
        if (item.trackCollider.maxZ > minZ && item.trackCollider.minZ < maxZ) out.push(item);
      }
    }
    return out;
  }

  all(): CollectibleInstance[] {
    return [...this.bySegment.values()].flat();
  }

  /** Marks the collectible as collected and returns its bottle value. `attracted`: pulled in by a magnet. */
  collect(item: CollectibleInstance, attracted = false): number {
    if (item.collected) return 0;
    item.collected = true;
    const bottleValue = this.config.bottleValue[item.definition.type];
    this.events.emit('collectible:collected', { collectible: item, bottleValue, attracted });
    // Hook for the golden-bottle action (later): the reward itself is the bottle value above.
    if (item.definition.type === CollectibleType.GoldenBottle) this.events.emit('collectible:golden', { collectible: item, bottleValue });
    return bottleValue;
  }

  /**
   * Brings the segment's floating collectibles above `height` (segment-local, m) down to `height` – their arc or roof
   * line belonged to an obstacle that was taken away (the ructec-Bulli's road): without it nothing reaches them.
   * Collectibles below stay as they are. Returns how many were lowered.
   */
  lowerFloating(segmentUid: number, height: number): number {
    let lowered = 0;
    for (const item of this.bySegment.get(segmentUid) ?? []) {
      const drop = item.localPosition.y - height;
      if (item.collected || drop <= 0) continue;
      item.localPosition.y = height;
      item.trackCollider.minY -= drop;
      item.trackCollider.maxY -= drop;
      lowered++;
    }
    return lowered;
  }

  private spawnFor(segment: SegmentInstance): void {
    const list: CollectibleInstance[] = [];
    for (const placement of segment.spawnPlan.collectibles) {
      const point = placement.point ?? segment.definition.collectibleSpawnPoints.find((p) => p.id === placement.spawnPointId);
      if (!point) throw new Error(`${segment.definition.id}: unknown collectible point ${placement.spawnPointId}`);
      const item = new CollectibleInstance(
        this.nextUid++,
        this.catalog.get(placement.collectibleId),
        segment,
        point,
        placement.heightOffset,
        placement.scale,
      );
      list.push(item);
      this.events.emit('collectible:spawned', { collectible: item });
    }
    this.bySegment.set(segment.uid, list);
  }

  private despawnFor(segment: SegmentInstance): void {
    const list = this.bySegment.get(segment.uid);
    if (!list) return;
    for (const item of list) this.events.emit('collectible:despawned', { collectible: item });
    this.bySegment.delete(segment.uid);
  }
}
